import type { FieldValues, Path } from 'react-hook-form'

import { type CommonInputProps, useInput } from '@subaio/components/src/HookForm'

import {
	ButtonClear,
	ButtonShow,
	ChangedIndicator,
	ColumnLabel,
	ControlContainer,
	FieldWrapper,
	HintContainer,
	InputComponent,
	InputContainer,
	InputError,
	Label,
	OptionalHint,
} from '../Shared'
import { Textarea } from './Textarea'

/**
 * A input component that supports several different HTML input `type`s.
 * Default return value is `undefined`
 * @example
 * ```tsx
 * <Input name='name' type='email' label='Email' />
 * ```
 */
export const Input = <TFieldValues extends FieldValues, TPath extends Path<TFieldValues>>({
	showOptionalHint: fieldShowOptionalHint,
	isFullWidth = true,
	isRequired = false,
	type = 'text',
	mode,
	isClearable = false,
	isDisabled = false,
	minLength,
	maxLength,
	name,
	autoComplete = 'off',
	isReadOnly = false,
	onBlur,
	onClick,
	label,
	hint,
	placeholder,
	shouldAutofocus,
	validate,
	minRows = 3,
	maxRows = 8,
	showChanged,
	showValidationMessage = true,
	testId,
	...rest
}: CommonInputProps<TPath>) => {
	const {
		id,
		inputRef,
		inputProps,
		fieldValue,
		hasError,
		isDirty,
		invalid,
		error,
		isFocusVisible,
		isFocused,
		isHovered,
		hoverProps,
		showChangedFields,
		showOptionalHint,
		canClear,
		clear,
		isValueVisible,
		toggleValueVisibility,
	} = useInput<TFieldValues, TPath>({
		showOptionalHint: fieldShowOptionalHint,
		isRequired,
		type,
		isClearable,
		isDisabled,
		minLength,
		maxLength,
		name,
		autoComplete,
		isReadOnly,
		onBlur,
		onClick,
		placeholder,
		shouldAutofocus,
		validate,
	})
	const labelId = `${id}-label`

	return (
		<FieldWrapper isFullWidth={isFullWidth}>
			<ColumnLabel>
				<Label htmlFor={id} id={labelId}>
					<span>
						{label}{' '}
						{(fieldShowOptionalHint ?? showOptionalHint) && !isRequired && <OptionalHint>(Optional)</OptionalHint>}
					</span>
				</Label>
				<InputContainer
					{...hoverProps}
					hasError={hasError}
					isDisabled={isDisabled}
					isHovered={isHovered}
					isFocusVisible={isFocusVisible}
					isFocus={isFocused}
					isReadOnly={isReadOnly}
					onClick={(event) => {
						if (event.defaultPrevented) return undefined
						inputRef.current?.focus()
					}}
					data-test-id={testId}
					{...rest}
				>
					{type === 'multiline' ? (
						<Textarea {...inputProps} hasError={hasError} minRows={minRows} maxRows={maxRows} />
					) : (
						<InputComponent {...inputProps} hasError={hasError} />
					)}
					<ControlContainer>
						{canClear && <ButtonClear onClick={clear} />}
						{type === 'password' && <ButtonShow isValueVisible={isValueVisible} onClick={toggleValueVisibility} />}
					</ControlContainer>
					{(showChanged ?? showChangedFields) && <ChangedIndicator show={isDirty} showInvalid={hasError || invalid} />}
				</InputContainer>
				<HintContainer isVisible={(Boolean(hint) || Boolean(maxLength)) && !hasError}>
					<span>{hint}</span>
					<span>
						{maxLength && (
							<>
								{(fieldValue && typeof fieldValue === 'string' && fieldValue.length) || 0} / {maxLength}
							</>
						)}
					</span>
				</HintContainer>
				<InputError
					hasError={showValidationMessage && hasError}
					errorMessage={error?.message}
					hasFocus={isFocusVisible}
				/>
			</ColumnLabel>
		</FieldWrapper>
	)
}
